import ActiveSurfaceView from 'mini-calendar-surface-store/lib/store/schema/ActiveSurfaceView';
import calendarEventsUpdated from '../actions/calendarEventsUpdated';
import calendarSelectionCompleted from '../actions/calendarSelectionCompleted';
import changeActiveSurface from 'mini-calendar-surface-store/lib/actions/changeActiveSurface';
import dateRangeChanged from 'mini-calendar-surface-store/lib/actions/dateRangeChanged';
import getLastActiveCalendarView from '../utils/getLastActiveCalendarView';
import initializeCalendarSurfaceView from 'mini-calendar-surface-store/lib/actions/initializeCalendarSurfaceView';
import refreshEventsCount from 'mini-calendar-surface-store/lib/actions/refreshEventsCount';
import setIsSurfaceReloadPending from 'mini-calendar-surface-store/lib/mutators/isSurfaceReloadPendingMutators';
import { DateRange } from 'mini-datetime-utils';
import { getCalendarSurfaceStore } from 'mini-calendar-surface-store';
import { lazyLoadDayView } from 'mini-calendar-dayview-store';
import { loadAgendaView } from 'mini-calendar-agenda-store/lib/actions/publicActions';
import { onAgendaViewScrollReachEnd } from 'mini-calendar-agenda-store/lib/actions/publicActions';
import { orchestrator } from 'satcheljs';
import { OwaDate, today } from 'owa-datetime';
import { reloadCalendarSurface } from 'mini-calendar-surface-store/lib/actions/publicActions';
import { setSelectedDate } from 'mini-calendar-surface-store/lib/mutators/dateMutators';
import updateRequestId from 'mini-calendar-surface-store/lib/mutators/updateRequestId';

/**
 * Utility to load active view by date
 * @param dateToLoad
 * @param activeSurfaceViewToPass
 */
function loadActiveViewByDate(dateToLoad: OwaDate, activeSurfaceViewToPass?: ActiveSurfaceView) {
    const { activeSurfaceView } = getCalendarSurfaceStore();
    updateRequestId();
    const surfaceView = activeSurfaceViewToPass ? activeSurfaceViewToPass : activeSurfaceView;
    switch (surfaceView) {
        case ActiveSurfaceView.AGENDA:
            loadAgendaView(dateToLoad);
            break;
        case ActiveSurfaceView.DAY:
            lazyLoadDayView.importAndExecute(dateToLoad);
            break;
    }
}

// exported only for testing purposes

export const initializeCalendarSurfaceViewOrchestrator = orchestrator(
    initializeCalendarSurfaceView,
    () => {
        // This is the scenario where anchor key is today date.
        // Need to make pivot based call using today date.
        const pivotDate = today();
        setSelectedDate(pivotDate);
        changeActiveSurface(getLastActiveCalendarView());
        loadActiveViewByDate(pivotDate);
        refreshEventsCount(pivotDate);
    }
);

export const dateRangeChangedOrchestrator = orchestrator(
    dateRangeChanged,
    (actionMessage: { newDateRange: DateRange; anchorDate?: OwaDate }) => {
        // This is the scenario where anchor key is selected date.
        // Need to make pivot based call using selected date.
        const { anchorDate } = actionMessage;

        loadActiveViewByDate(anchorDate);

        refreshEventsCount(anchorDate);
    }
);

export const onAgendaViewScrollReachEndOrchestrator = orchestrator(
    onAgendaViewScrollReachEnd,
    actionMessage => {
        updateRequestId();
        // This is the scenario where agenda view reached scroll end.
        loadAgendaView(null /* pivot date not present */, actionMessage.scrollDirection);
    }
);

export const calendarSelectionCompleteOrchestrator = orchestrator(
    calendarSelectionCompleted,
    () => {
        updateRequestId();
        const { activeSurfaceView, selectedDate } = getCalendarSurfaceStore();
        switch (activeSurfaceView) {
            case ActiveSurfaceView.AGENDA:
                loadAgendaView();
                break;
            case ActiveSurfaceView.DAY:
                lazyLoadDayView.importAndExecute(selectedDate);
                break;
        }
        refreshEventsCount(selectedDate, true /* forceRefresh */);
    }
);

const reloadCalendarSurfaceInternal = events => {
    // This is the scenario where agenda view/day view needs to be reloaded since events store changed.
    const { activeSurfaceView, selectedDate } = getCalendarSurfaceStore();
    updateRequestId();
    switch (activeSurfaceView) {
        case ActiveSurfaceView.AGENDA:
            loadAgendaView(
                null /* pivot date not present */,
                null /* scroll direction not present */,
                events
            );
            break;
        case ActiveSurfaceView.DAY:
            lazyLoadDayView.importAndExecute(selectedDate, events);
            break;
    }
    refreshEventsCount(selectedDate, true /* forceRefresh */);
};

/**
 * Reloads calendar surface by listening to calendarEventsUpdated action from DAL
 */
export const calendarEventsUpdatedOrchestrator = orchestrator(
    calendarEventsUpdated,
    actionMessage => {
        const { isReadingPaneVisible, isComposeVisible } = getCalendarSurfaceStore();
        // Don't process reload request if surface is not the current active view
        // But set the reload pending flag so that it will be used in future when surface becomes active again
        if (isReadingPaneVisible || isComposeVisible) {
            setIsSurfaceReloadPending(true);
            return;
        }
        reloadCalendarSurfaceInternal(actionMessage.events);
    }
);

/**
 * Reloads calendar surface by listening to reloadCalendarSurface action
 */
export const reloadCalendarSurfaceOrchestrator = orchestrator(reloadCalendarSurface, () =>
    reloadCalendarSurfaceInternal(null)
);

export const changeSurfaceViewOrchestrator = orchestrator(changeActiveSurface, actionMessage => {
    const { newSurface: activeSurfaceView } = actionMessage;
    const { selectedDate } = getCalendarSurfaceStore();
    loadActiveViewByDate(selectedDate, activeSurfaceView);
});
